import { frame, hit, placeLabels, toScreen } from '@versesverse/core';
import type { Camera, Placed, PlacedLabel, Viewport } from '@versesverse/core';
import type { RenderEdge, RenderNode } from '@versesverse/core';

/* This module is DOM-free on purpose: it runs BOTH on the main thread and inside the OffscreenCanvas
 * worker, so it may not touch `window`/`document`. It draws through the narrow DrawContext below, which
 * a real 2D context (window or worker) satisfies. The dev hot-reload that used to live here — forcing a
 * full page reload when the draw engine changed — now lives in MapCanvas, which is main-thread-only. */

/*
 * THE DRAW ENGINE, WHERE "NO JANK" IS A PROPERTY AND NOT A HOPE.
 *
 * The old page recomputed everything on every input event and drew unbatched — 13,000 visibility
 * evaluations and 2,799 stroke flushes per frame, on every drag (ARCHITECTURE.md §Phase 3). This
 * is built so that cannot happen, and so a test can PROVE it did not:
 *
 *   - Interaction is camera-only. pan()/zoomAt() move the camera and nothing else — no visibility
 *     recompute, no layout, no label placement. The counters below stay flat across a pan.
 *   - Visibility is cached. It is computed once, in setFilter(); a draw never recomputes it.
 *   - Edges are batched. They are bucketed by width once, and each bucket strokes as ONE path, so
 *     the number of stroke() calls is a small constant, whatever the edge count.
 *   - Labels are skipped while moving. draw({ interacting: true }) measures no text at all.
 *   - Off-screen nodes and edges are culled.
 *
 * The stats are the controller's own — it knows what it drew — so a test asserts against them
 * without instrumenting the canvas. The geometry (frame/toScreen/hit/placeLabels) is core's.
 */

/** The slice of a 2D context this draws through — narrow, so a test passes a stub and the real ctx fits. */
export interface DrawContext {
  save(): void;
  restore(): void;
  clearRect(x: number, y: number, w: number, h: number): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  quadraticCurveTo(cpx: number, cpy: number, x: number, y: number): void;
  arc(x: number, y: number, r: number, start: number, end: number): void;
  rect(x: number, y: number, w: number, h: number): void;
  closePath(): void;
  fill(): void;
  stroke(): void;
  fillText(text: string, x: number, y: number): void;
  measureText(text: string): { width: number };
  fillStyle: string | CanvasGradient | CanvasPattern;
  strokeStyle: string | CanvasGradient | CanvasPattern;
  lineWidth: number;
  globalAlpha: number;
}

export interface MapStats {
  /** Total draws issued. */
  draws: number;
  /** Times visibility was recomputed — must grow only on a filter change, never on interaction. */
  visibilityComputes: number;
  /** stroke() calls issued in total — bounded per draw by the number of width buckets. */
  strokeBatches: number;
  /** measureText() calls — zero while interacting. */
  labelMeasures: number;
  /**
   * NAMES ACTUALLY DRAWN on the last settled frame, and the zoom they were chosen at.
   *
   * `labelMeasures` counts candidates OFFERED to the placer, which is a different quantity and was
   * the only one reported — so the map could measure 213 names and draw none, and its own report
   * would look busy. That is exactly the state a phone was in on 2026-08-22: 213 measures at 390px
   * and not one name on screen, with nothing able to say whether the placer had returned nothing or
   * the draw was landing somewhere invisible.
   *
   * Kept beside the zoom because the gate that chooses names is `weight × zoom`, and a count of
   * names is only interpretable next to the number that decided it — this file's own recurring
   * lesson about a number and the quantity it is a number of.
   */
  labelsDrawn: number;
  /** The camera zoom the last frame was drawn at. */
  zoom: number;
  /**
   * WHERE THE CAMERA WAS, in world coordinates.
   *
   * Reported for the same reason the zoom is: a count of names is only interpretable beside the
   * numbers that decided it, and after `zoom` and `nodesDrawn` were both ruled out, the centre is
   * the only thing left that can explain a fresh phone load drawing 9 names and a reader who
   * arrived at `?node=` drawing 1. Two sessions bounded that question by elimination and could not
   * close it, because nothing reported this.
   */
  camX: number;
  camY: number;
  /** Nodes and edges actually drawn on the last frame (after culling). */
  nodesDrawn: number;
  edgesDrawn: number;
  /**
   * Times the projection scratch buffer had to be reallocated.
   *
   * The buffer is reused across buckets and across frames; if it were rebuilt per frame this is
   * where the GC pauses would come from, and a pause is the jank the engine exists to rule out. It
   * is counted rather than timed because the claim is about ALLOCATION: a wall-clock test of it
   * measures the machine, and a busy machine then fails a correct engine.
   */
  strandGrowths: number;
}

const EDGE_COLOR = '#9aa0aa';
/*
 * The web is a faint texture, not a wall. Opacity is floored low and graded by the edge's own width
 * — which already encodes tie strength (edgeWidth) — so the dense mesh of weak ties recedes and only
 * the strong ones carry. Grading by the width bucket keeps it one stroke per bucket: no per-edge
 * alpha, no extra flush. (The old flat 0.28 was the "edges obscure everything" wall.)
 */
const EDGE_ALPHA_FLOOR = 0.03;
const EDGE_ALPHA_SLOPE = 0.05;
const EDGE_ALPHA_MAX = 0.15;
/*
 * And fainter still with distance. With the whole universe in view, every one of thousands of edges
 * is on screen at once and their sum is a grey fog over the centre — the "edges obscure everything"
 * complaint that a per-edge opacity alone cannot answer. So edges whisper when zoomed out and firm
 * up as the reader zooms into a region, where the density is low enough to read.
 */
const EDGE_ZOOM_FULL = 1.5;
/* Floored at zero: with the whole universe in view the web fades to nothing, so the opening frame is
   a calm field of figures — not a grey mesh. It firms up only as the reader zooms into a region. */
const EDGE_MIN_FADE = 0;
/* And graded by whether an edge aims at the heart of the cosmos: a spoke running toward the centre
   carries at full opacity; a tangent crossing the rim side-on recedes, so the eye follows the flow
   inward rather than the mesh around the edge. Indexed by the radial tier (0 spoke … 2 tangent). */
const EDGE_RADIAL_TIERS = [1, 0.62, 0.36];
/*
 * Every edge bows by this fraction of its own length, so routes read as arcs that flow instead of
 * straight lines meeting at hard angles. Short links stay nearly straight; long ones sweep. A
 * quadratic curve is still one sub-path in the batched stroke, so the bounded-stroke budget holds.
 */
const EDGE_BOW = 0.12;
/* A selected node's own edges — redrawn in its colour, brighter and a touch thicker — are the ONLY
   web shown while it is selected; the rest steps aside, so its constellation is what the eye follows. */
const SELECTED_EDGE_ALPHA = 0.9;
const SELECTED_EDGE_WIDTH = 1.6;
/*
 * STRANDS: light gathering between two figures, not wiring bolted to them.
 *
 * Three changes from a plain bow. A strand starts CLEAR of both discs, so nothing touches a figure
 * it does not belong to. It is brightest in the middle and fades to nothing at both ends, so it
 * reads as light gathering rather than a line terminating. And the unselected web sits well under
 * a selected one — the same material at lower opacity, rather than a second treatment.
 *
 * WHY THE TAPER IS SEGMENTED AND NOT A GRADIENT. The design gets it from `createLinearGradient`
 * per edge. Two things make that unportable here, and both are structural: `DrawContext` exposes
 * no gradient factory (it is satisfied by a real 2D context, the OffscreenCanvas worker context
 * and the test stubs, and widening it widens every stub), and one gradient object per edge means
 * one `stroke()` per edge — the 2,799-flush regression ARCHITECTURE.md §Phase 3 removed and
 * `controller.test.ts` asserts against. So each edge contributes three sub-arcs to three passes,
 * each stroked at its own alpha. The cost is 3× the bucket count, still a constant independent of
 * how many edges there are.
 */
const STRAND_GAP = 6;
const STRAND_SEGMENTS = 3;
const STRAND_SEGMENT_ALPHA = [0.26, 1, 0.26];
/* The middle carries the width; the tails thin slightly, which is what reads as a taper. */
const STRAND_SEGMENT_WIDTH = [0.78, 1, 0.78];
/* Shorter than this and there is no room for two gaps and anything between — it would read as a
   speck rather than a strand, so it is not drawn at all. */
const STRAND_MIN_LENGTH = 14;
/** Numbers per edge in the projection scratch buffer: ax, ay, bx, by, gapA, gapB. */
const STRAND_STRIDE = 6;
/*
 * A node never shrinks below MIN_NODE_R on screen, so zooming out leaves a field of legible points
 * rather than sub-pixel dust — the "nodes seem to vanish" complaint. And it never grows by more than
 * NODE_GROW_MAX×, so zooming in scales a node up without letting one disc swallow the view. Both are
 * the old map's values, which read as intuitive; the base radius already encodes verse count.
 */
const MIN_NODE_R = 2.4;
const NODE_GROW_MAX = 3.2;
/*
 * THE BRIDGES GLOW. A figure's colour says which scripture it is most named in; its GLOW says how many
 * it spans. A figure in one scripture has none — it is simply its colour. A figure the traditions
 * share carries a soft warm halo, wider and brighter the more scriptures it bridges, so the map's most
 * cross-tradition figures — Abraham, Moses, Mary — read as its luminous stars rather than hiding as
 * one more blue dot. Drawn as a few flat translucent discs (no per-frame gradient), behind the fill.
 */
const GLOW_COLOR = '#fff2d8'; // pale warm-white: reads as light, not the brown a tan-gold makes over an orange disc
const GLOW_REACH_BASE = 1.7; // halo radius as a multiple of the node radius, at two scriptures
const GLOW_REACH_PER = 0.7; // added per scripture beyond two
const GLOW_MAX_REACH = 22; // px cap, so a zoomed-in halo stays a luminous accent, not a brown wash
const GLOW_LAYER_ALPHA = 0.055; // per-layer opacity; the layers overlap into a soft centre-bright falloff
const GLOW_LAYERS = 5; // more, fainter layers → a smoother falloff, not a stepped blob
/* Gold — the map's mark for a disputed cross-scripture identity. It no longer competes with the shared
   glow, because a disputed figure suppresses its own glow (see drawNodes), so gold reads cleanly as
   "there is a question here". A crisp ring just outside the disc with a faint outer halo, so a contested
   figure is a BEACON across the field. Each of the two rings strokes as one batched path. */
const CONTESTED_RING_COLOR = '#ffd166';
const CONTESTED_RING_WIDTH = 2;
const CONTESTED_RING_PAD = 3;
const CONTESTED_HALO_PAD = 4; // the faint outer ring, further out again — what catches the eye
const CONTESTED_HALO_ALPHA = 0.4;
/*
 * THE ARC RING: which scriptures name this figure, said on the disc itself.
 *
 * The glow says HOW MANY traditions a figure bridges. It cannot say which, and "which" is the
 * question a reader of this map is actually asking. So a figure named by more than one text wears
 * a ring of arcs outside its disc, one arc per text in its own colour, in registry order — the
 * same order the wheel runs round the map, so the ring and the map read the same way.
 *
 * Drawn per corpus rather than per node: all the Hebrew Bible arcs on every figure are one path
 * and one stroke, so the whole ring layer costs one flush per text however many figures wear it.
 * Below MIN_ARC_R there is no room to tell arcs apart and they would read as a smudge, so they are
 * dropped rather than drawn wrong.
 */
const MIN_ARC_R = 3.2;
const ARC_PAD_MIN = 2.6;
const ARC_PAD_SHARE = 0.42;
const ARC_WIDTH_MIN = 1.2;
const ARC_WIDTH_MAX = 3.2;
const ARC_WIDTH_SHARE = 0.2;
/** The break between one text's arc and the next, so the ring reads as segments and not a circle. */
const ARC_GAP_MAX = 0.3;
const ARC_GAP_SHARE = 1.05;
/*
 * THE APERTURE: a figure every tradition names is drawn OPEN — a dark core inside a bright rim,
 * rather than one more coloured disc.
 *
 * 63 of 1,488 qualify. That rarity is the point: Adam, Abraham, Moses, David, Jesus stop being
 * blue dots among six hundred and become the places the traditions are all looking at. A dark core
 * because the disc's colour says which scripture names it MOST, and a figure all three read is not
 * most anyone's.
 */
const APERTURE_CORE = '#06070f';
const APERTURE_RIM = 'rgba(222,234,255,.62)';
const APERTURE_RIM_SHARE = 0.1;
const APERTURE_RIM_MIN = 1;
/*
 * THE TIER RINGS: the ladder, drawn faintly, so distance from the centre reads as a scale.
 *
 * A figure's radius means how near the divine it stands. Without something to read it against
 * that is a fact the map knows and the reader cannot see — every figure is simply somewhere. The
 * rings are that scale, and they are deliberately UNLABELLED: the orientation pill already names
 * the rung the reader is on, and a ring with a caption would say it twice and crowd the field.
 *
 * (The banner that fired on crossing into a rung used to be the thing that named it; it went on
 * 2026-08-22. The pill remains, so the reason these stay unlabelled is unchanged.)
 *
 * DASHED WITHOUT `setLineDash`. `DrawContext` does not expose it, for the same reason it exposes
 * no gradient factory — the interface is satisfied by a real 2D context, the worker's
 * OffscreenCanvas context and the test stubs, and every addition widens all three. So the dash is
 * built the way the strand taper was: as the segments themselves, added to one batched path. Every
 * ring at a given zoom shares an alpha, so the whole ladder is ONE stroke.
 */
const RING_COLOR = '#92a8d6';
/** Alpha by zoom band: firm when the whole map is in view, a whisper once the reader is inside it. */
const RING_ALPHA_FAR = 0.3;
const RING_ALPHA_MID = 0.2;
const RING_ALPHA_NEAR = 0.08;
const RING_ZOOM_FAR = 0.35;
const RING_ZOOM_MID = 0.5;
/** The design's `[2, 8]`: two pixels of ink, eight of gap. */
const RING_DASH = 2;
const RING_DASH_PERIOD = 10;
/** Below this a ring is a smudge rather than a scale, so it is not drawn. */
const RING_MIN_SCREEN_R = 26;
/*
 * THE SCRIPTURE WASHES: where each text's names are thickest, behind everything else.
 *
 * At the zoom where an individual figure is two pixels, the map must still be able to say where
 * the Quran's names are and where the Talmud's are. The washes say it. They fade out entirely as
 * the reader comes in, because once names are legible the names themselves are the answer and a
 * coloured fog over them is only in the way.
 *
 * THE FALLOFF IS LAYERED DISCS, NOT A GRADIENT — `DrawContext` exposes no gradient factory, for
 * the same reason it exposes no `setLineDash`. `drawGlow` already solved this shape of problem the
 * same way. The design's profile is alpha 1 at the centre, 0.38 at 0.45 of the reach, 0 at the
 * edge; these radii are that curve inverted, so N nested discs each at `peak / N` accumulate to
 * roughly it — six layers give 1.0, 0.33 and 0 at those three points.
 */
const WASH_LAYERS = [1, 0.76, 0.52, 0.36, 0.24, 0.12];
/** Peak opacity at the very centre of the densest clump, before the zoom fade. */
const WASH_PEAK = 0.16;
/**
 * How strength follows density: the faintest clump still shows, the densest carries the peak.
 *
 * RELATIVE TO THE DENSEST CLUMP ON THIS MAP, not an absolute count. The design brightens by
 * `min(1, 0.4 + n × 0.1)`, which reaches full strength at six figures in a cell — right for its
 * own 300-name roster, and flat for this library's 1,488, where 74% of clumps cleared six and the
 * layer would render as one even blanket. Scaling against the densest keeps the variation at any
 * roster size. The share is square-rooted for the same reason the reach is: a wash is read as an
 * area, so four times the figures should read as twice the mark, not four times.
 */
const WASH_DENSITY_FLOOR = 0.4;
const WASH_DENSITY_SPAN = 0.6;
/*
 * HOW FAR IN THE WASHES SURVIVE — as a MULTIPLE of the zoom that fits the whole field.
 *
 * A wash says where a scripture's names are thickest ACROSS THE MAP. That is a statement about the
 * field, and it stops being one the moment the field is off screen: closed in on one neighbourhood
 * the layer is a broad tint over ground whose actual figures the reader can see. So it holds full
 * strength while most of the map is in view and is gone by the time a quarter of it is.
 *
 * IT WAS AN ABSOLUTE ZOOM (gone at 0.66, fading from 0.30) and that is the third time this exact
 * mistake has been found in this port — see deviations 6 and 7. A zoom number is only meaningful
 * against a world of a particular size: at this roster's scale the fit is about 0.2, so the washes
 * survived to three times the fit and were drawn at three times the radius, which measured at
 * TWENTY-NINE SCREENS of overdraw per frame and took the opening flight to twenty frames a second.
 * As a ratio it means the same thing at any roster size, and costs a bounded amount at every zoom.
 */
const WASH_GONE_AT_FIT = 2.2;
const WASH_FULL_TO_FIT = 1.2;
/** Below this a wash is a coloured dot rather than a field, so it is not drawn. */
const WASH_MIN_SCREEN_R = 14;
/*
 * THE DUST BEYOND THE RIM — the map admitting it is not everything.
 *
 * Proportions from the design, expressed against THIS map's rim rather than its world units, so
 * the haze stays just outside the naming however the roster grows. Its own seed, so the sky is the
 * same sky on every visit.
 */
const DUST_SEED = 9091;
const DUST_COUNT = 52;
/** Where the haze begins and how far it spreads, as multiples of the outermost figure's radius. */
const DUST_INNER = 1.9;
const DUST_SPAN = 1.2;
/** A perfect ring of haze reads as a drawn circle, so it is squashed the way the design squashes it. */
const DUST_SQUASH = 0.86;
const DUST_SIZE_MIN = 0.018;
const DUST_SIZE_SPAN = 0.05;
const DUST_BRIGHT_MIN = 0.05;
const DUST_BRIGHT_SPAN = 0.12;
const DUST_COLOR = '#d0defa';
const DUST_LAYERS = [1, 0.72, 0.46, 0.24];
/** How much further out than the rim the reader must see before the haze is at full strength. */
const DUST_FADE_SPAN = 0.8;
/** Below this a speck is a pixel of noise rather than distant light. */
const DUST_MIN_SCREEN_R = 3;
/*
 * THE FOG — how much of a figure a reader who has not been there gets to see.
 *
 * The design's numbers. Within reach is dim but aimable-at: it has to be possible to choose where
 * to go next. Beyond is a whisper — enough that the shape of the whole is there, not enough to
 * read. The gap between them is what makes the frontier legible without drawing a line round it.
 */
const FOG_WITHIN_REACH = 0.42;
const FOG_BEYOND = 0.1;
/*
 * SECTOR LABELS: the map names its own sky.
 *
 * Which direction means which scripture is the one thing about this map a reader cannot work out
 * by looking. Every other claim it makes is legible — a colour, a ring, a distance — but an angle
 * says nothing until something names it. So each wedge carries its text's name, and beneath it how
 * many figures that text alone holds, which is the difference between a scripture that mostly
 * shares its people and one that mostly does not.
 *
 * Shown only from far out. Close in, the reader is inside one sector and reading figures; a
 * caption floating over them names something they are no longer asking about.
 */
const SECTOR_LABEL_COLOR_DIM = 'rgba(150,164,192,.5)';
const SECTOR_ZOOM_GONE = 0.4;
const SECTOR_ZOOM_SPAN = 0.1;
/**
 * The retry ladder: pull the label in, then swing it off its own axis.
 *
 * Radius alone is not enough, and the design records why — the Mishnah's wedge points straight
 * down into the corpus filter bar, so every radius along that line is blocked and the label simply
 * never places. Swinging it round the wedge is what frees it. Tried nearest-first in both, so a
 * label only moves as far as it has to.
 */
const SECTOR_RADIUS_TRIES = [1, 0.86, 0.72, 0.6];
const SECTOR_ANGLE_TRIES = [0, 0.34, -0.34, 0.6, -0.6];
/** Half the room a label needs, beyond its measured text — the gap that keeps two from touching. */
const SECTOR_LABEL_PAD = 16;
/** Clear of the viewport edge, so a label is never half off the screen. */
const SECTOR_EDGE_INSET = 8;
/** A wedge narrower than this cannot hold a label pointing along it without leaning into its
 *  neighbour's, so it goes unnamed rather than mislabelling the sky beside it. */
const SECTOR_MIN_WIDTH = 0.12;
const LABEL_COLOR = '#c9c4bb';
/*
 * WHICH NAMES EARN INK, AND WHEN. Zoomed out, a name shows only if its node is intrinsically
 * prominent — base radius (verse count) × zoom clearing a few pixels — so the map names its
 * Abrahams and Davids, not every minor figure it can find a gap for. Past LABEL_ZOOM_ALL the reader
 * has clearly leaned in, so everything that fits is fair game and collision alone decides. The
 * selected name is always kept. This is the old map's gate; the current one had none, which is why
 * zooming out buried the map in labels.
 */
const LABEL_MIN_SCREEN_R = 3.5;
const LABEL_ZOOM_ALL = 2;

/**
 * WHICH PART OF A CIRCLE IS ON SCREEN — the angular window, or null if none of it is.
 *
 * Zoomed right in, a tier ring is many times the size of the viewport: its circumference at the
 * outermost rung passes 50,000 pixels, which at one dash per ten pixels would be five thousand
 * sub-paths per ring per frame, essentially all of them off screen. Emitting them and letting the
 * canvas clip them is the kind of waste that does not show up until a phone is holding the frame.
 *
 * Solved against the viewport's BOUNDING CIRCLE rather than its rectangle — conservative, so a
 * little more is drawn than strictly needed, and it falls out of one inequality instead of four
 * line-segment intersections. A point on the ring is inside that circle when
 * `cos(θ − φ) ≥ (R² + d² − Rv²) / (2 R d)`, with `d` the distance from the ring's centre to the
 * viewport's and `φ` its direction. Above 1 nothing is visible; below −1 all of it is.
 */
function visibleArc(cx: number, cy: number, r: number, W: number, H: number): { from: number; to: number } | null {
  const viewR = Math.hypot(W, H) / 2;
  const ux = W / 2 - cx;
  const uy = H / 2 - cy;
  const d = Math.hypot(ux, uy);
  /* The ring is centred on the viewport: every angle is equally close, so all of it is in play. */
  if (d < 1e-6) return r <= viewR + r ? { from: 0, to: TAU } : null;
  const k = (r * r + d * d - viewR * viewR) / (2 * r * d);
  if (k > 1) return null;
  if (k < -1) return { from: 0, to: TAU };
  const half = Math.acos(k);
  const phi = Math.atan2(uy, ux);
  return { from: phi - half, to: phi + half };
}

/** An edge's opacity, from its width bucket (which encodes tie strength): weak ties faint, strong clear. */
function edgeAlphaForWidth(width: number): number {
  return Math.min(EDGE_ALPHA_MAX, EDGE_ALPHA_FLOOR + width * EDGE_ALPHA_SLOPE);
}
const MIN_ZOOM = 0.05;
const MAX_ZOOM = 8;
const TAU = Math.PI * 2;

/*
 * THE FLY-TO-A-NODE TWEEN. Selecting a node moves the camera to it — but a cut is disorienting
 * (you lose where it was), so the camera travels there. Below a few pixels there is nothing worth
 * animating; above that the duration grows with the on-screen distance but is capped, so a nudge
 * is quick and a leap across the map is smooth without ever dragging.
 */
const FOCUS_SNAP_PX = 4;
const FOCUS_MIN_MS = 320;
const FOCUS_MAX_MS = 760;
const FOCUS_MS_PER_PX = 0.35;
/*
 * Selecting a figure from the whole-universe overview used to centre it and leave it a speck in a
 * sea of names. The fly now also zooms IN to a level where the figure and its neighbourhood fill the
 * view — but only zooms in: a reader already deep in a region keeps their zoom, the camera never
 * pulls back out from under them.
 */
const FOCUS_ZOOM = 1.2;

/** Ease-out cubic: quick to leave, gentle to arrive — the shape a camera move should have. */
export function easeOutCubic(t: number): number {
  const u = 1 - t;
  return 1 - u * u * u;
}

/** A node's index into the array, kept beside a filter predicate. */
export type NodeFilter = (node: RenderNode) => boolean;

/**
 * One density clump of one scripture, in world units and already wearing its colour.
 *
 * Resolved outside the controller, from `Bundle.fields` and the palette, so the renderer holds no
 * corpus registry — exactly as `RenderNode.corpusColors` is resolved in core rather than here.
 */
/**
 * One wedge of the map, and what its label says — from `Bundle.wheel`, with its colour resolved.
 *
 * `label` is the text's name as the reader should see it, so the renderer neither holds a corpus
 * registry nor decides how a scripture is spelled.
 */
export interface Sector {
  id: string;
  label: string;
  /** The wedge's centre line, in radians. */
  a: number;
  width: number;
  /** How far out the label sits, in world units. */
  r: number;
  /** How many figures this text alone names. */
  only: number;
  colour: string;
}

export interface Wash {
  x: number;
  y: number;
  /** Reach in world units. */
  r: number;
  /** How many of that text's figures gathered here — what sets the strength. */
  n: number;
  colour: string;
}

/**
 * Where the reader is, said as a figure rather than as a camera reading.
 *
 * Deliberately small: an id to compare against the last one, the rung to name, and the texts that
 * name it. It crosses the worker boundary on every change, and a whole `RenderNode` would carry a
 * label, a colour, a shape and a position for the sake of three fields.
 */
export interface Orientation {
  id: string;
  tier: number;
  /** The texts naming this figure. The chrome names one only when exactly one does. */
  corpora: string[];
}

function orientationOf(n: RenderNode): Orientation {
  return { id: n.id, tier: n.tier, corpora: n.corpora };
}

/** One text's slice of one figure's arc ring, collected so the whole layer can batch by colour. */
interface ArcSegment {
  x: number;
  y: number;
  r: number;
  width: number;
  from: number;
  to: number;
}

/** A node that made it onto the screen this frame, with what the label pass needs to rank it. */
interface OnScreenNode {
  id: string;
  text: string;
  x: number;
  y: number;
  /** On-screen radius — positions the label above the disc and is what collision tests against. */
  r: number;
  /** Intrinsic importance: the base radius (verse count), used by the gate and the ranking so a node
   *  floored small when zoomed out still counts for what it is, not what it currently draws as. */
  weight: number;
  /** The selected node — its label is never suppressed. */
  pinned: boolean;
  /** A disputed figure — its name is shown at every zoom and ranked first, so you read WHO is disputed. */
  contested: boolean;
}

export class MapController {
  private readonly nodes: RenderNode[];
  private readonly edges: RenderEdge[];
  /** Edge indices grouped by (rounded width, radial tier), so each group draws as one stroked path. */
  private readonly edgeBuckets: Map<string, { width: number; radial: number; indices: number[] }>;
  /** The heart of the cosmos (centroid of the figures), so edges can be graded by whether they aim at it. */
  private readonly centerX: number;
  private readonly centerY: number;
  /** Each rung's settled radius in world units, innermost first — the scale the rings draw. */
  private readonly tierRadii: number[];
  /** Whether the positions carry a claim — more than one rung means they were anchored. */
  private readonly anchored: boolean;
  /** How far the outermost figure sits from the centre — where the naming stops. */
  private readonly rim: number;
  /**
   * The dust beyond the rim, built once and kept.
   *
   * NOT named for anything else in this class, and that is deliberate: the design records losing a
   * canvas to a nebula cache and a journey record both called `deep`, which threw on every
   * zoom-out frame — the exact frames this is read on.
   */
  private dustCache: { x: number; y: number; rx: number; brightness: number }[] | null = null;
  /** Where each scripture's names are thickest, already resolved to a colour. */
  private readonly washes: Wash[];
  /** The busiest clump on the map — what every other wash's strength is read against. */
  private readonly densestWash: number;
  /** The wedges, and what each one's label says. */
  private readonly sectors: Sector[];
  /** Screen rectangles the chrome covers — no name may be placed under one. */
  private chrome: { x0: number; y0: number; x1: number; y1: number }[] = [];
  /** What the reader has opened, and what one step from it reaches. Empty until they have walked. */
  private walked: ReadonlySet<string> = new Set();
  private within: ReadonlySet<string> = new Set();
  /** Whether to dim at all. False for a reader who has been nowhere, so the map is whole. */
  private fogged = false;
  /** How much of the fog is in force — 0 the whole field lit, 1 as specified. See `setFog`. */
  private fogMix = 1;
  private readonly placedById: Placed[];
  /** Node id → its index, so selection and highlight are O(1), not a scan per frame. */
  private readonly indexById: Map<string, number>;
  /** The selected node, whose incident edges are drawn in its colour; null when nothing is selected. */
  private selectedId: string | null = null;
  private view: Viewport = { width: 1, height: 1 };
  private cam: Camera = { x: 0, y: 0, z: 1 };
  /** An in-flight fly-to-node tween (position and zoom), or undefined when the camera is at rest. */
  private focusAnim?: { fromX: number; fromY: number; fromZ: number; toX: number; toY: number; toZ: number; startMs: number; durMs: number };
  private visible: boolean[];
  /**
   * Projected edge geometry for the bucket being drawn, reused across buckets and across frames.
   *
   * A Float64Array rather than objects, and one buffer rather than one per bucket, because this is
   * touched once per visible edge per frame: allocating here is how a draw loop earns a GC pause.
   * It grows to fit the largest bucket and never shrinks.
   */
  private strandBuffer = new Float64Array(0);
  private readonly stats: MapStats = {
    draws: 0, visibilityComputes: 0, strokeBatches: 0, labelMeasures: 0, labelsDrawn: 0, zoom: 0, camX: 0, camY: 0, nodesDrawn: 0, edgesDrawn: 0,
    strandGrowths: 0,
  };

  constructor(nodes: RenderNode[], edges: RenderEdge[], washes: Wash[] = [], sectors: Sector[] = []) {
    this.nodes = nodes;
    this.edges = edges;
    this.washes = washes;
    this.sectors = sectors;
    /* Floored at 1 so an empty map, or one whose clumps all hold nothing, cannot divide by zero. */
    this.densestWash = Math.max(1, ...washes.map(w => w.n));
    this.visible = nodes.map(() => true);
    /* Placed[] for the geometry helpers — the same objects, viewed as what core/map.ts reads. */
    this.placedById = nodes.map(n => ({ id: n.id, x: n.x, y: n.y, r: n.r, label: n.label }));
    this.indexById = new Map(nodes.map((n, i) => [n.id, i]));

    /* The centroid — the heart the spokes run toward. */
    let sx = 0, sy = 0;
    for (const n of nodes) { sx += n.x; sy += n.y; }
    this.centerX = nodes.length ? sx / nodes.length : 0;
    this.centerY = nodes.length ? sy / nodes.length : 0;

    /*
     * WHERE EACH RUNG ACTUALLY SETTLED — measured, not read from a table.
     *
     * The layout ASKS for a radius per rung and the crowd argues with it, so a ring drawn at the
     * asked-for radius would sit beside the figures rather than through them. Taking the mean of
     * what each rung came to rest at puts the scale where the reader can see it is a scale.
     *
     * MEASURED FROM THE WORLD ORIGIN, NOT THE CENTROID, and the difference is not academic. The
     * anchors place every figure at a radius from (0, 0); the centroid is wherever the crowd
     * happens to sit, and the crowd is not evenly spread — the wedges are sized by how many names
     * face each text, so the Hebrew Bible's is 189° and the Quran's is 11°. Measured on the real
     * roster the centroid lands 290 units from the origin, which is FURTHER than the whole radius
     * of the divine ring. Rings drawn around it were centred on nothing, and the radii taken from
     * it were nonsense: tier 0 spans 214..595 from the origin and 21..845 from the centroid.
     *
     * Caught by looking at a screenshot. Every test passed, because every test measured the rings
     * against the same wrong centre the code used.
     *
     * Computed once here: it depends on the nodes, which never move, so a per-frame recompute
     * would be the same answer at 1,488 hypots a frame.
     */
    const byTier = new Map<number, { sum: number; count: number }>();
    for (const n of nodes) {
      const d = Math.hypot(n.x, n.y);
      const seen = byTier.get(n.tier);
      if (seen) { seen.sum += d; seen.count++; } else byTier.set(n.tier, { sum: d, count: 1 });
    }
    this.tierRadii = [...byTier.entries()]
      .map(([, { sum, count }]) => sum / count)
      .filter(r => r > 0)
      .sort((a, b) => a - b);
    /* More than one rung means the layout was ANCHORED — every figure put where it was told rather
       than left where its ties stopped. Derived rather than passed, so it cannot disagree with the
       positions it describes. */
    this.anchored = byTier.size > 1;
    /* Measured from the origin, as the rungs are — the same point every anchor's radius is from. */
    this.rim = nodes.reduce((far, n) => Math.max(far, Math.hypot(n.x, n.y)), 0);

    /* Bucket by width AND radial tier, so a tangential edge is drawn fainter than a spoke of the same
       weight, while each (width, tier) group is still one stroked path — the batching is preserved. */
    this.edgeBuckets = new Map();
    edges.forEach((e, i) => {
      const width = Math.max(0.5, Math.round(e.width * 2) / 2);
      const tier = this.radialTier(e);
      const key = `${width}|${tier}`;
      const bucket = this.edgeBuckets.get(key);
      if (bucket) bucket.indices.push(i);
      else this.edgeBuckets.set(key, { width, radial: EDGE_RADIAL_TIERS[tier] ?? 1, indices: [i] });
    });
  }

  /**
   * How much an edge aims at the heart of the cosmos, as a tier: 0 a spoke (runs toward or away from
   * the centre), 2 a tangent (crosses it side-on), 1 in between. The angle is taken at the edge's
   * midpoint against the direction to the centre; an edge sitting on the centre is a spoke by default.
   * Computed once per edge at construction, never per frame.
   */
  private radialTier(e: RenderEdge): number {
    const a = this.nodes[e.a];
    const b = this.nodes[e.b];
    if (!a || !b) return 0;
    const ex = b.x - a.x, ey = b.y - a.y;
    const rx = this.centerX - (a.x + b.x) / 2, ry = this.centerY - (a.y + b.y) / 2;
    const edgeLen = Math.hypot(ex, ey);
    const radialLen = Math.hypot(rx, ry);
    if (edgeLen < 1e-6 || radialLen < 1e-6) return 0;
    const align = Math.abs((ex * rx + ey * ry) / (edgeLen * radialLen)); // |cos|: 1 spoke, 0 tangent
    return align > 0.66 ? 0 : align > 0.33 ? 1 : 2;
  }

  /**
   * How many stroke batches a single draw can cost — the constant edge-drawing is bounded by.
   *
   * Multiplied by the segment count because a strand tapers by being stroked in three passes rather
   * than by carrying a gradient. Still a constant: it does not move with the number of edges, which
   * is the whole property the bound exists to state.
   */
  get bucketCount(): number {
    return this.edgeBuckets.size * STRAND_SEGMENTS;
  }

  snapshot(): MapStats {
    return { ...this.stats };
  }

  setViewport(width: number, height: number): void {
    this.view = { width: Math.max(1, width), height: Math.max(1, height) };
    this.fitCache = undefined;
  }

  camera(): Camera {
    return { ...this.cam };
  }

  /**
   * Fit the visible nodes to the viewport. A deliberate action (load, reset), not interaction.
   *
   * KEEPS ALL OF AN ANCHORED MAP, and only most of an unanchored one. `frame` defaults to framing
   * 96% of the mass and letting the rest fall off the edge, which is right when the tail is
   * stragglers — weakly-tied names that drifted, where including them would shrink the interesting
   * middle to nothing. An anchored layout has no stragglers: every figure is on a rung it was put
   * on, and the outermost rung is the statement that the peoples stand at the edge of the naming.
   * Cropping 4% there crops that rung, which is the one thing on the map that cannot be inferred
   * from what is left.
   */
  resetCamera(): void {
    this.cam = this.fitCamera();
  }

  /**
   * Where the camera sits when the whole field is on screen. The reference every tour stop uses,
   * and the scale the washes fade against.
   *
   * CACHED, because the washes ask for it once per frame and `frame` sorts every coordinate twice.
   * It depends on the viewport and on which nodes are shown, and on nothing else — so those two are
   * the only places that drop it.
   */
  private fitCache?: Camera;

  private fitCamera(): Camera {
    if (this.fitCache) return this.fitCache;
    const shown = this.placedById.filter((_, i) => this.visible[i]);
    this.fitCache = frame(shown.length ? shown : this.placedById, this.view, this.anchored ? { keep: 1 } : {});
    return this.fitCache;
  }

  /**
   * THE TOUR'S CAMERA — a world point, or the whole field, at a multiple of the zoom that fits it.
   *
   * `to` is a world point to centre on, or NULL for wherever the field itself is framed. Two kinds
   * of target because the tour says two kinds of thing: "all of it" and "that, over there". The
   * field's own framing is neither the origin nor a number the main thread can know — it is the
   * centroid of the names, offset again to clear the chrome — so it has to be asked for by name
   * rather than computed on the other side of the worker boundary and left to go stale.
   *
   * `zoom` IS ALWAYS A RATIO of the zoom that fits the whole field, for either kind of target. An
   * absolute zoom is a number tuned to one roster — the design's own are, and at this library's
   * scale its first sweep would frame a single figure. As a ratio the flight reads the same however
   * the roster grows. So `flyTo(null, 1)` is exactly `resetCamera`, and `flyTo(node, 4)` is four
   * times as close, on that figure.
   *
   * `durMs` is given rather than derived from the distance: the tour's beats are fixed, and a
   * duration computed from how far the camera happens to be would arrive early over a near stop
   * and late over a far one, against a schedule that is the same either way.
   */
  flyTo(to: { x: number; y: number } | null, zoom: number, options: { now?: number; durMs?: number } = {}): void {
    const fit = this.fitCamera();
    const toZ = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, fit.z * zoom));
    const toX = to ? to.x : fit.x;
    const toY = to ? to.y : fit.y;
    const durMs = options.durMs ?? FOCUS_MAX_MS;
    /*
     * NOTHING TO MOVE IS NOT A MOVE. The flight opens by holding the whole field — which is where
     * the camera already is, because the map framed itself on load — so this was tweening the
     * camera from a point to that same point for three and a half seconds, redrawing the entire
     * field sixty times a second to show a picture that never changed. `focusOn` has always
     * snapped on a move too small to watch; this is the same rule, and the same reason.
     */
    const stillPx = Math.hypot(toX - this.cam.x, toY - this.cam.y) * toZ;
    if (durMs <= 0 || (stillPx < FOCUS_SNAP_PX && Math.abs(toZ - this.cam.z) < 0.002)) {
      this.focusAnim = undefined;
      this.cam = { x: toX, y: toY, z: toZ };
      return;
    }
    this.focusAnim = {
      fromX: this.cam.x, fromY: this.cam.y, fromZ: this.cam.z,
      toX, toY, toZ,
      startMs: options.now ?? 0,
      durMs,
    };
  }

  /** Move the camera by a screen delta. Interaction: recomputes NOTHING but the camera. */
  pan(dxScreen: number, dyScreen: number): void {
    this.cam = { ...this.cam, x: this.cam.x - dxScreen / this.cam.z, y: this.cam.y - dyScreen / this.cam.z };
  }

  /** Zoom by a factor, keeping the world point under (sx, sy) fixed. Interaction: camera only. */
  zoomAt(sx: number, sy: number, factor: number): void {
    const wx = (sx - this.view.width / 2) / this.cam.z + this.cam.x;
    const wy = (sy - this.view.height / 2) / this.cam.z + this.cam.y;
    const z = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, this.cam.z * factor));
    this.cam = { x: wx - (sx - this.view.width / 2) / z, y: wy - (sy - this.view.height / 2) / z, z };
  }

  /**
   * Centre the camera on a node, keeping the zoom — for a search result, a deep link, or a tap.
   *
   * `animate` opts into the fly-to tween; the default is an instant cut, so a caller that just
   * wants the camera placed (and the tests that assert it) get exactly that. When animating, the
   * move is set up here and advanced frame by frame by `advanceFocus`; `now` is the clock the
   * tween starts from, passed in so it can be driven deterministically in a test. A move shorter
   * than a few pixels snaps regardless — there is nothing there to watch.
   */
  focusOn(id: string, options: { animate?: boolean; now?: number; inset?: { right: number; bottom: number } } = {}): boolean {
    const node = this.nodes.find(n => n.id === id);
    if (!node) return false;

    /*
     * Land the node at the centre of the VISIBLE area, not the raw viewport. The right panel (a
     * bottom sheet on a phone) covers part of the screen, and a node placed dead-centre hides under
     * it. Shifting the camera by half the covered extent — in world units, so it holds at any zoom —
     * puts the node in the middle of what the reader can actually see.
     */
    /* Only ever zoom in: bring the overview to a comfortable level, but leave a reader who is already
       closer than that exactly where they are. The inset offset uses the TARGET zoom, so the node
       lands centred in the visible area at the zoom it finishes on. */
    const toZ = Math.min(MAX_ZOOM, Math.max(this.cam.z, FOCUS_ZOOM));
    const inset = options.inset;
    const toX = node.x + (inset ? inset.right / 2 / toZ : 0);
    const toY = node.y + (inset ? inset.bottom / 2 / toZ : 0);

    const distancePx = Math.hypot(toX - this.cam.x, toY - this.cam.y) * toZ;
    /* Snap only when there is nothing to see move — neither a pan of a few pixels nor a zoom change. */
    if (!options.animate || (distancePx < FOCUS_SNAP_PX && Math.abs(toZ - this.cam.z) < 0.02)) {
      this.focusAnim = undefined;
      this.cam = { x: toX, y: toY, z: toZ };
      return true;
    }

    this.focusAnim = {
      fromX: this.cam.x,
      fromY: this.cam.y,
      fromZ: this.cam.z,
      toX,
      toY,
      toZ,
      startMs: options.now ?? 0,
      durMs: Math.min(FOCUS_MAX_MS, FOCUS_MIN_MS + distancePx * FOCUS_MS_PER_PX),
    };
    return true;
  }

  /**
   * Advance an in-flight fly-to tween to time `now`, and report whether it is still going.
   *
   * Called once at the top of every frame: when nothing is animating it does nothing and returns
   * false, so the draw loop settles; while a tween runs it eases the camera toward the target and
   * returns true, which is the caller's signal to schedule another frame. The last step lands the
   * camera exactly on the target and returns false.
   */
  advanceFocus(now: number): boolean {
    const anim = this.focusAnim;
    if (!anim) return false;

    const t = anim.durMs <= 0 ? 1 : Math.min(1, Math.max(0, (now - anim.startMs) / anim.durMs));
    const k = easeOutCubic(t);
    this.cam = {
      x: anim.fromX + (anim.toX - anim.fromX) * k,
      y: anim.fromY + (anim.toY - anim.fromY) * k,
      z: anim.fromZ + (anim.toZ - anim.fromZ) * k,
    };
    if (t >= 1) {
      this.focusAnim = undefined;
      return false;
    }
    return true;
  }

  /**
   * Set (or clear) the selected node. Interaction-cheap: it recomputes nothing — the highlight is
   * decided at draw time from this id — so selecting during a pan cannot stutter.
   */
  setSelected(id: string | null): void {
    this.selectedId = id;
  }

  /**
   * WHERE THE CHROME COVERS THE MAP, in screen pixels.
   *
   * The canvas fills the viewport and every control floats over it, so a name placed under the
   * search box is drawn and then covered — ink spent on something nobody can read, and a place
   * taken from a figure that could have been named there. The bands are measured on the main
   * thread (only it has a DOM) and handed here, and both the figure names and the wedge names are
   * placed around them.
   *
   * Cheap to call: it stores a list and nothing else. The caller decides when the chrome has moved.
   */
  setChrome(bands: readonly { x0: number; y0: number; x1: number; y1: number }[]): void {
    this.chrome = bands.map(b => ({ ...b }));
  }

  /**
   * WHAT THE READER HAS UNCOVERED — full strength walked, dimmer within reach, a whisper beyond.
   *
   * The map is something you uncover by walking rather than something you filter, and the fog is
   * what makes that true rather than merely said. A figure you have opened is fully there; one a
   * step away is visible enough to aim at; the rest is the shape of a world you have not been to.
   *
   * OFF UNTIL SOMEBODY HAS WALKED. Passing no walked ids leaves every figure at full strength,
   * which is what a map with no arrival should look like — switched on for a reader who has been
   * nowhere it would dim the entire field to a tenth and read as broken rather than as unexplored.
   *
   * `mix` IS HOW MUCH OF THE FOG IS IN FORCE, and it exists for exactly one moment: the survey
   * shows the reader the whole field and then lets the dark close back in over it. A cut there
   * would read as a bug — the map flickering — where a ramp reads as the point being made. The
   * caller eases it; this only has to hold a number, so nothing here animates and no frame loop is
   * kept alive for a layer that changes six times in a lifetime.
   */
  setFog(walked: ReadonlySet<string>, within: ReadonlySet<string>, mix = 1): void {
    this.walked = walked;
    this.within = within;
    this.fogged = walked.size > 0;
    this.fogMix = Math.min(1, Math.max(0, mix));
  }

  /** One figure's share of the fog. 1 when nobody has walked, so the map is whole by default. */
  private fogFor(id: string): number {
    if (!this.fogged) return 1;
    if (this.walked.has(id)) return 1;
    const deep = this.within.has(id) ? FOG_WITHIN_REACH : FOG_BEYOND;
    /* Interpolated from FULLY LIT rather than toward black: mix 0 is the whole field visible, which
       is the state the survey hands over, and mix 1 is the fog as the design specifies it. */
    return 1 - this.fogMix * (1 - deep);
  }

  /** Recompute which nodes are shown. The ONE place visibility changes — counted, so a test proves it. */
  setFilter(predicate: NodeFilter): void {
    this.visible = this.nodes.map(predicate);
    this.stats.visibilityComputes++;
    this.fitCache = undefined;
  }

  /**
 * WHAT LIES BEYOND THE EDGE OF WHAT IS NAMED.
 *
 * Faint smudges of light out past the outermost rung — unmapped, and deliberately so. The map's
 * rim is where the naming stops, not where the world does, and a field that simply ended in black
 * would say the opposite: that this library is everything there is. 253 transcripts argue about
 * the Talmud and this map carries a fraction of it; texts sit unshelved for want of a licence.
 * The dust is the one place the map admits that.
 *
 * SHOWN WHEN THE READER CAN SEE PAST THE RIM, which is the honest translation of the design's own
 * gate. The design fades it in below `floor × 2.6`, where the floor drops as a reader visits more
 * — that floor belongs to the onboarding walk, which does not exist here yet, so copying the
 * number would have gated the layer on a variable that is always the same. "Far enough out to see
 * the edge" is what the number was for, and it can be asked directly.
 */
private drawDust(ctx: DrawContext, W: number, H: number): void {
  if (this.rim <= 0) return;
  /* How far the reader can see, in world units. Past the rim means the edge is on screen. */
  const seeing = Math.hypot(W, H) / 2 / this.cam.z;
  const out = Math.min(1, Math.max(0, (seeing / this.rim - 1) / DUST_FADE_SPAN));
  if (out <= 0.02) return;

  const specks = this.dust();
  ctx.fillStyle = DUST_COLOR;
  for (const speck of specks) {
    const r = speck.rx * this.cam.z;
    if (r < DUST_MIN_SCREEN_R) continue;
    const p = toScreen(speck, this.cam, this.view);
    if (p.x + r < 0 || p.x - r > W || p.y + r < 0 || p.y - r > H) continue;
    /* Layered flat discs rather than a gradient, as the washes and the glow already are —
       `DrawContext` exposes no gradient factory and this is the third layer not to need one. */
    ctx.globalAlpha = (speck.brightness * out) / DUST_LAYERS.length;
    for (const layer of DUST_LAYERS) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, r * layer, 0, TAU);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

/**
 * The specks, built once from a fixed seed and kept.
 *
 * Deterministic so the sky is the same sky on every visit — a field that reshuffled itself each
 * time the reader zoomed out would read as noise rather than as a place. Built lazily because a
 * reader who never pulls back past the rim never needs it.
 */
private dust(): { x: number; y: number; rx: number; brightness: number }[] {
  if (this.dustCache) return this.dustCache;
  /* The seeded LCG the layout uses, so there is one kind of reproducible randomness here. */
  let seed = DUST_SEED >>> 0;
  const rnd = (): number => (seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296;
  const specks: { x: number; y: number; rx: number; brightness: number }[] = [];
  for (let i = 0; i < DUST_COUNT; i++) {
    const angle = rnd() * TAU;
    /* Sized against THIS map's rim rather than the design's world units, so the dust sits just
       beyond the naming whatever the roster grows to. */
    const radius = this.rim * (DUST_INNER + Math.pow(rnd(), 0.7) * DUST_SPAN);
    specks.push({
      x: Math.cos(angle) * radius,
      /* Squashed, as the design has it: a perfect ring of haze reads as a drawn circle. */
      y: Math.sin(angle) * radius * DUST_SQUASH,
      rx: this.rim * (DUST_SIZE_MIN + rnd() * DUST_SIZE_SPAN),
      brightness: DUST_BRIGHT_MIN + rnd() * DUST_BRIGHT_SPAN,
    });
  }
  this.dustCache = specks;
  return specks;
}

/**
 * WHICH FIGURE THE READER IS LOOKING AT — the selected one, or the nearest to the centre of view.
 *
 * The map's chrome has to name where the reader is, and the honest answer is a FIGURE rather than
 * a camera reading. Distance from the centre is a number the camera knows; "you are among the
 * names of God" is a claim about the map, and it can only be made by looking at what is actually
 * in front of the reader. A camera sitting in a gap between two rungs has a radius and no answer.
 *
 * Selected first, because a reader who opened a figure is looking at that one whatever the camera
 * is centred on.
 *
 * O(n) in the figures, so this is for a caller that asks a few times a second — not per frame.
 */
inFront(): Orientation | null {
  const selected = this.selectedId === null ? undefined : this.indexById.get(this.selectedId);
  if (selected !== undefined && this.visible[selected]) return orientationOf(this.nodes[selected]!);

  let best: RenderNode | null = null;
  let nearest = Infinity;
  for (let i = 0; i < this.nodes.length; i++) {
    if (!this.visible[i]) continue;
    const n = this.nodes[i]!;
    const d = Math.hypot(n.x - this.cam.x, n.y - this.cam.y);
    if (d < nearest) { nearest = d; best = n; }
  }
  return best ? orientationOf(best) : null;
}

/** What a tap at (sx, sy) landed on, among the visible nodes — or null. */
  hitTest(sx: number, sy: number): string | null {
    const shown = this.placedById.filter((_, i) => this.visible[i]);
    return hit(shown, sx, sy, this.cam, this.view)?.id ?? null;
  }

  draw(ctx: DrawContext, options: { interacting?: boolean } = {}): void {
    const interacting = options.interacting ?? false;
    const { width: W, height: H } = this.view;
    this.stats.draws++;

    ctx.save();
    ctx.clearRect(0, 0, W, H);

    /* Furthest back of all: the unmapped light past the edge of the naming, which is behind even
       the ground the map sits on. Then the washes, then the ladder — a scale drawn over the
       figures would be a grid on top of a map rather than under it. */
    this.drawDust(ctx, W, H);
    this.drawWashes(ctx, W, H);
    this.drawTierRings(ctx, W, H);
    /* The whole web is drawn EVERY frame as gentle bows — in motion exactly as at rest. Both the curve
       flattening and the weak-tie decimation that used to lighten a drag were main-thread savings; the
       draw runs on a worker thread now, so the web no longer changes shape or thins while the map moves. */
    this.drawEdges(ctx, W, H);
    this.drawSelectedEdges(ctx, W, H);
    const onScreen: OnScreenNode[] = this.drawNodes(ctx, W, H);
    if (!interacting) {
      /* The sector names go down BEFORE the figure names, and claim their boxes first: which
         direction means which scripture is the orienting fact, and a figure's name losing its
         place to it is the right trade. */
      const claimed = this.drawSectorLabels(ctx, W, H);
      this.drawLabels(ctx, onScreen, claimed);
    }

    ctx.restore();
  }

  /**
   * The scripture washes: a soft field of each text's colour where its names gather.
   *
   * Fills, not strokes, so there is no flush to batch — but each clump still costs `WASH_LAYERS`
   * fills, and there are at most sixteen clumps per text. Culled hard: gone entirely once the
   * reader has zoomed in, and skipped individually when off screen or too small to be a field.
   */
  private drawWashes(ctx: DrawContext, W: number, H: number): void {
    if (this.washes.length === 0) return;
    const z = this.cam.z;
    /* Full strength with the whole map in view, nothing at all once the names are legible. Measured
       against the zoom that FITS the field, so it says the same thing at any roster size. */
    const closeness = z / this.fitCamera().z;
    const fade = Math.min(1, Math.max(0, (WASH_GONE_AT_FIT - closeness) / (WASH_GONE_AT_FIT - WASH_FULL_TO_FIT)));
    if (fade <= 0.02) return;

    for (const wash of this.washes) {
      const r = wash.r * z;
      if (r < WASH_MIN_SCREEN_R) continue;
      const p = toScreen(wash, this.cam, this.view);
      if (p.x + r < 0 || p.x - r > W || p.y + r < 0 || p.y - r > H) continue;
      const share = Math.sqrt(wash.n / this.densestWash);
      const peak = WASH_PEAK * fade * Math.min(1, WASH_DENSITY_FLOOR + WASH_DENSITY_SPAN * share);
      ctx.fillStyle = wash.colour;
      /* Nested discs at a constant share of the peak: they accumulate into the design's falloff
         without a gradient object. Largest first, so the centre ends up carrying every layer. */
      ctx.globalAlpha = peak / WASH_LAYERS.length;
      for (const layer of WASH_LAYERS) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * layer, 0, TAU);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }

  /**
   * The ladder, as dashed rings — one batched stroke for all of them.
   *
   * Every ring at a given zoom shares an alpha, so they go into a single path and cost one flush
   * however many rungs there are.
   */
  private drawTierRings(ctx: DrawContext, W: number, H: number): void {
    if (this.tierRadii.length === 0) return;
    const z = this.cam.z;
    const alpha = z < RING_ZOOM_FAR ? RING_ALPHA_FAR : z < RING_ZOOM_MID ? RING_ALPHA_MID : RING_ALPHA_NEAR;
    /* The world origin, which is the point every anchor's radius was measured from — NOT the
       centroid, which sits 290 units away on the real roster because the wedges are unequal. */
    const centre = toScreen({ x: 0, y: 0 }, this.cam, this.view);

    ctx.strokeStyle = RING_COLOR;
    ctx.globalAlpha = alpha;
    ctx.lineWidth = 1;
    ctx.beginPath();
    let any = false;
    for (const worldR of this.tierRadii) {
      const r = worldR * z;
      /* Too small to read as a scale — it would be a smudge round the centre. */
      if (r < RING_MIN_SCREEN_R) continue;
      const window = visibleArc(centre.x, centre.y, r, W, H);
      if (!window) continue; // the whole ring is off screen
      /* Step by arc LENGTH, not by a fixed count, so the dash pattern stays [2, 8] pixels at every
         zoom rather than stretching with the ring. */
      const step = RING_DASH_PERIOD / r;
      for (let a = window.from; a < window.to; a += step) {
        ctx.moveTo(centre.x + Math.cos(a) * r, centre.y + Math.sin(a) * r);
        ctx.arc(centre.x, centre.y, r, a, a + RING_DASH / r);
        any = true;
      }
    }
    if (any) { ctx.stroke(); this.stats.strokeBatches++; }
    ctx.globalAlpha = 1;
  }

  private drawEdges(ctx: DrawContext, W: number, H: number): void {
    /* A selection spotlights its own constellation (drawSelectedEdges); the whole web steps aside so
       the reader follows the one thing they asked about, not a thousand things they did not. */
    if (this.selectedId !== null) { this.stats.edgesDrawn = 0; return; }
    ctx.strokeStyle = EDGE_COLOR;
    /* Whisper when the whole universe is in view, firm up when zoomed into a region. */
    const zoomFade = Math.max(EDGE_MIN_FADE, Math.min(1, this.cam.z / EDGE_ZOOM_FULL));
    let edgesDrawn = 0;
    /* The full web is drawn in motion too — no weak-tie decimation. It used to skip the faint width-1.0
       mesh (~72% of edges) while moving to spare the main thread; the worker draws it all now, so the
       web no longer thins on a drag and fills back in on release. */
    for (const { width, radial, indices } of this.edgeBuckets.values()) {
      /* PROJECT ONCE, STROKE THREE TIMES. The taper needs three passes over the same edges, and
         projecting inside that loop would triple the per-edge arithmetic — the expensive half —
         to buy nothing, since the geometry is identical on every pass. So the bucket's visible
         edges are projected into a scratch buffer first and the passes read from that. The buffer
         is reused across frames and buckets: a per-frame allocation of this size is what the GC
         pauses are made of. */
      const projected = this.projectBucket(indices, W, H);
      if (!projected) continue;
      for (let segment = 0; segment < STRAND_SEGMENTS; segment++) {
        ctx.lineWidth = width * STRAND_SEGMENT_WIDTH[segment]!;
        ctx.globalAlpha = edgeAlphaForWidth(width) * radial * zoomFade * STRAND_SEGMENT_ALPHA[segment]!;
        ctx.beginPath();
        let any = false;
        for (let k = 0; k < projected; k++) {
          const at = k * STRAND_STRIDE;
          const drawn = this.traceStrand(
            ctx,
            this.strandBuffer[at]!, this.strandBuffer[at + 1]!,
            this.strandBuffer[at + 2]!, this.strandBuffer[at + 3]!,
            this.strandBuffer[at + 4]!, this.strandBuffer[at + 5]!,
            segment,
          );
          if (drawn) { any = true; if (segment === 0) edgesDrawn++; }
        }
        if (any) { ctx.stroke(); this.stats.strokeBatches++; }
      }
    }
    ctx.globalAlpha = 1;
    this.stats.edgesDrawn = edgesDrawn;
  }

  /**
   * Project one bucket's visible, on-screen edges into the scratch buffer, and say how many landed.
   *
   * Six numbers per edge: both projected centres and the gap each end must clear. The gaps are
   * computed here rather than in `traceStrand` because they come from the node radii, which the
   * three segment passes would otherwise each recompute identically.
   */
  private projectBucket(indices: number[], W: number, H: number): number {
    let count = 0;
    const need = indices.length * STRAND_STRIDE;
    if (this.strandBuffer.length < need) { this.strandBuffer = new Float64Array(need); this.stats.strandGrowths++; }
    for (const i of indices) {
      const e = this.edges[i]!;
      if (!this.visible[e.a] || !this.visible[e.b]) continue;
      const na = this.nodes[e.a]!;
      const nb = this.nodes[e.b]!;
      const a = toScreen(na, this.cam, this.view);
      const b = toScreen(nb, this.cam, this.view);
      /* Cull a segment with both ends off the same edge of the screen — cheap and conservative. */
      if ((a.x < 0 && b.x < 0) || (a.x > W && b.x > W) || (a.y < 0 && b.y < 0) || (a.y > H && b.y > H)) continue;
      const at = count * STRAND_STRIDE;
      this.strandBuffer[at] = a.x;
      this.strandBuffer[at + 1] = a.y;
      this.strandBuffer[at + 2] = b.x;
      this.strandBuffer[at + 3] = b.y;
      this.strandBuffer[at + 4] = this.screenRadius(na) + STRAND_GAP;
      this.strandBuffer[at + 5] = this.screenRadius(nb) + STRAND_GAP;
      count++;
    }
    return count;
  }

  /**
   * Add ONE segment of one strand to the current path, as a sub-arc of the same bow.
   *
   * The full curve is P0 → C → P1 with C the midpoint pushed perpendicular by EDGE_BOW, always the
   * same rotational way, so the whole field of lines flows rather than crossing at hard angles. It
   * is then trimmed by gapA/gapB so it starts clear of both discs, and the remaining span is split
   * into STRAND_SEGMENTS parts; `segment` says which part to add.
   *
   * The sub-arc is a REAL quadratic, its control point taken from the standard split, so curvature
   * is preserved exactly. Sampling it as line segments instead kinks visibly at these lengths —
   * a third of a short bow is short enough that the flat is obvious.
   *
   * Part of the batched path; it never strokes. Returns whether it added anything, so the caller
   * knows not to stroke an empty path and can count the edge exactly once.
   */
  private traceStrand(
    ctx: DrawContext,
    ax: number, ay: number, bx: number, by: number,
    gapA: number, gapB: number, segment: number,
  ): boolean {
    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.hypot(dx, dy);
    if (len < gapA + gapB + STRAND_MIN_LENGTH) return false;
    const cx = (ax + bx) / 2 - dy * EDGE_BOW;
    const cy = (ay + by) / 2 + dx * EDGE_BOW;

    const u0 = gapA / len;
    const u1 = 1 - gapB / len;
    const span = (u1 - u0) / STRAND_SEGMENTS;
    const s0 = u0 + span * segment;
    const s1 = s0 + span;

    const at = (t: number, p: number, c: number, q: number): number => {
      const k = 1 - t;
      return k * k * p + 2 * k * t * c + t * t * q;
    };
    /* The quadratic restricted to [s0, s1] is itself a quadratic; this is its control point. */
    const sub = (p: number, c: number, q: number): number =>
      (1 - s0) * (1 - s1) * p + (s0 * (1 - s1) + s1 * (1 - s0)) * c + s0 * s1 * q;

    ctx.moveTo(at(s0, ax, cx, bx), at(s0, ay, cy, by));
    ctx.quadraticCurveTo(sub(ax, cx, bx), sub(ay, cy, by), at(s1, ax, cx, bx), at(s1, ay, cy, by));
    return true;
  }

  /**
   * The selected node's own edges, drawn over the dimmed web in the node's colour so WHICH nodes it
   * reaches reads at a glance — the connection legibility that was lost. One batched stroke: however
   * many edges the node has, the highlight costs a single flush. Nothing when no node is selected,
   * or when the selected node is filtered out (there is nothing on screen to trace from).
   */
  private drawSelectedEdges(ctx: DrawContext, W: number, H: number): void {
    if (this.selectedId === null) return;
    const selIdx = this.indexById.get(this.selectedId);
    if (selIdx === undefined || !this.visible[selIdx]) return;

    ctx.strokeStyle = this.nodes[selIdx]!.color;
    /* The SAME strand renderer, at a higher opacity — so the selected fan and the ambient web are
       one material at two strengths rather than two treatments that have to be kept looking alike. */
    const fan: number[] = [];
    for (let i = 0; i < this.edges.length; i++) {
      const e = this.edges[i]!;
      if (e.a !== selIdx && e.b !== selIdx) continue;
      fan.push(i);
    }
    const projected = this.projectBucket(fan, W, H);
    if (!projected) return;
    for (let segment = 0; segment < STRAND_SEGMENTS; segment++) {
      ctx.globalAlpha = SELECTED_EDGE_ALPHA * STRAND_SEGMENT_ALPHA[segment]!;
      ctx.lineWidth = SELECTED_EDGE_WIDTH * STRAND_SEGMENT_WIDTH[segment]!;
      ctx.beginPath();
      let any = false;
      for (let k = 0; k < projected; k++) {
        const at = k * STRAND_STRIDE;
        if (this.traceStrand(
          ctx,
          this.strandBuffer[at]!, this.strandBuffer[at + 1]!,
          this.strandBuffer[at + 2]!, this.strandBuffer[at + 3]!,
          this.strandBuffer[at + 4]!, this.strandBuffer[at + 5]!,
          segment,
        )) any = true;
      }
      if (any) { ctx.stroke(); this.stats.strokeBatches++; }
    }
    ctx.globalAlpha = 1;
  }

  /**
   * A node's on-screen geometry: its projected centre and the floored, growth-capped radius — or null
   * if the disc is fully off-screen. The ONE place this is computed, shared by the Canvas draw loop and
   * the label layout, so a name always sits on the disc the eye actually sees, at either renderer.
   * Floored so a node stays legible zoomed out, growth capped so zooming in never lets one disc balloon
   * over the view — the base radius already carries the verse-count weight.
   */
  private screenNode(n: RenderNode, W: number, H: number): { x: number; y: number; r: number } | null {
    const p = toScreen(n, this.cam, this.view);
    const r = this.screenRadius(n);
    if (p.x + r < 0 || p.x - r > W || p.y + r < 0 || p.y - r > H) return null;
    return { x: p.x, y: p.y, r };
  }

  /**
   * The radius a node DRAWS at, floored and growth-capped.
   *
   * Extracted so the disc and the strand gap read it from ONE place. The gap has to clear the disc
   * actually drawn, and a second copy of the floor and the cap would hold at some zooms and part
   * company at the extremes — exactly where the floor and the cap are doing something.
   */
  private screenRadius(n: RenderNode): number {
    return Math.max(MIN_NODE_R, n.r * Math.min(NODE_GROW_MAX, this.cam.z));
  }

  /** The label-ranking view of a node at its projected position — shared so the draw loop and the GPU
   *  overlay agree on which figures earn a name and where it sits. */
  private onScreenOf(n: RenderNode, x: number, y: number, r: number): OnScreenNode {
    return { id: n.id, text: n.label, x, y, r, weight: n.r, pinned: n.id === this.selectedId, contested: n.contested ?? false };
  }

  private drawNodes(ctx: DrawContext, W: number, H: number): OnScreenNode[] {
    const onScreen: OnScreenNode[] = [];
    const contestedRings: { x: number; y: number; r: number }[] = [];
    /* Arcs are collected and drawn per corpus AFTER the discs, so the ring layer costs one flush
       per text rather than one per figure — and so no disc can be painted over its own ring. */
    const arcs = new Map<string, ArcSegment[]>();
    const apertures: { x: number; y: number; r: number }[] = [];
    let nodesDrawn = 0;
    for (let i = 0; i < this.nodes.length; i++) {
      if (!this.visible[i]) continue;
      const n = this.nodes[i]!;
      const s = this.screenNode(n, W, H);
      if (!s) continue; // off-screen
      /* Shared figures glow behind their disc — kept even while the map moves, so the luminous
         bridges do not blink out on every pan. It is the EDGES, not the glow, that were the heavy
         per-frame cost (2,799 stroked curves), and those are what motion skips. A disputed figure
         never glows — its ring is its one signal (its drawer still says which scriptures it is in). */
      const spans = n.corpora.length;
      if (spans >= 2 && !n.contested) this.drawGlow(ctx, s.x, s.y, s.r, spans);
      /* Dimmed when nobody has checked the figure — the same question the ported renderer asks. */
      ctx.globalAlpha = (n.checked ? 1 : 0.5) * this.fogFor(n.id);
      /* A figure every tradition names is drawn OPEN: the dark core reads as a way through rather
         than as a disc, because its colour would otherwise claim one scripture names it most. */
      ctx.fillStyle = n.shared ? APERTURE_CORE : n.color;
      this.drawShape(ctx, n.shape, s.x, s.y, s.r);
      if (n.shared) apertures.push({ x: s.x, y: s.y, r: s.r });
      if (n.contested) contestedRings.push({ x: s.x, y: s.y, r: s.r + CONTESTED_RING_PAD });
      this.collectArcs(arcs, n, s.x, s.y, s.r);
      nodesDrawn++;
      onScreen.push(this.onScreenOf(n, s.x, s.y, s.r));
    }
    ctx.globalAlpha = 1;
    this.drawApertureRims(ctx, apertures);
    this.drawArcs(ctx, arcs);
    this.drawContestedRings(ctx, contestedRings);
    this.stats.nodesDrawn = nodesDrawn;
    this.stats.zoom = this.cam.z;
    this.stats.camX = this.cam.x;
    this.stats.camY = this.cam.y;
    return onScreen;
  }

  /**
   * Note this figure's share of each corpus arc, bucketed by corpus so the ring layer batches.
   *
   * The ring divides the full turn between the texts naming this figure, starting due up so the
   * first text's arc begins where the wheel does. Each arc is shortened by a gap at both ends, so
   * the ring reads as separate claims rather than as one circle: a figure in four texts must not
   * look like a figure in one with a halo.
   */
  private collectArcs(into: Map<string, ArcSegment[]>, n: RenderNode, x: number, y: number, r: number): void {
    const count = n.corpusColors.length;
    if (count < 2 || r < MIN_ARC_R) return;
    const ringR = r + Math.max(ARC_PAD_MIN, r * ARC_PAD_SHARE);
    const step = TAU / count;
    const gap = Math.min(ARC_GAP_MAX, ARC_GAP_SHARE / count);
    const width = Math.max(ARC_WIDTH_MIN, Math.min(ARC_WIDTH_MAX, r * ARC_WIDTH_SHARE));
    for (let i = 0; i < count; i++) {
      const colour = n.corpusColors[i]!;
      const bucket = into.get(colour);
      const segment: ArcSegment = {
        x, y, r: ringR, width,
        from: i * step - Math.PI / 2 + gap / 2,
        to: (i + 1) * step - Math.PI / 2 - gap / 2,
      };
      if (bucket) bucket.push(segment); else into.set(colour, [segment]);
    }
  }

  /**
   * The arc rings, one stroked path per corpus.
   *
   * Grouped by colour rather than by figure because a stroke can carry only one style: drawing
   * per figure would be one flush per multi-text name — hundreds — where this is one per text.
   * The line width varies with the disc, so within a colour the arcs are grouped again by width;
   * that is a handful of buckets, not hundreds, because the width is quantised by the radius.
   */
  private drawArcs(ctx: DrawContext, arcs: Map<string, ArcSegment[]>): void {
    if (arcs.size === 0) return;
    ctx.globalAlpha = 1;
    for (const [colour, segments] of arcs) {
      ctx.strokeStyle = colour;
      const byWidth = new Map<number, ArcSegment[]>();
      for (const seg of segments) {
        /* Quantised to a tenth of a pixel: a stroke width finer than that is not a thing anyone
           can see, and rounding it is what keeps the bucket count small. */
        const key = Math.round(seg.width * 10) / 10;
        const bucket = byWidth.get(key);
        if (bucket) bucket.push(seg); else byWidth.set(key, [seg]);
      }
      for (const [width, group] of byWidth) {
        ctx.lineWidth = width;
        ctx.beginPath();
        for (const seg of group) {
          /* moveTo before each arc, or consecutive arcs join into one scribbled path. */
          ctx.moveTo(seg.x + Math.cos(seg.from) * seg.r, seg.y + Math.sin(seg.from) * seg.r);
          ctx.arc(seg.x, seg.y, seg.r, seg.from, seg.to);
        }
        ctx.stroke();
        this.stats.strokeBatches++;
      }
    }
  }

  /** The bright rim that turns a dark disc into an opening. One batched stroke for all of them. */
  private drawApertureRims(ctx: DrawContext, apertures: { x: number; y: number; r: number }[]): void {
    if (apertures.length === 0) return;
    ctx.strokeStyle = APERTURE_RIM;
    ctx.globalAlpha = 1;
    /* One width for all of them: the rim is a mark, not a measurement, and grouping by width here
       would add flushes to say something no reader would read. */
    ctx.lineWidth = Math.max(APERTURE_RIM_MIN, apertures[0]!.r * APERTURE_RIM_SHARE);
    ctx.beginPath();
    for (const a of apertures) {
      ctx.moveTo(a.x + a.r, a.y);
      ctx.arc(a.x, a.y, a.r, 0, TAU);
    }
    ctx.stroke();
    this.stats.strokeBatches++;
  }

  /**
   * A shared figure's halo: a few flat translucent discs, largest to smallest, that overlap into a
   * soft centre-bright glow. Reach and layering grow with how many scriptures the figure spans, so the
   * most cross-tradition figures burn brightest. No gradient object built per frame — just fills.
   */
  private drawGlow(ctx: DrawContext, x: number, y: number, r: number, spans: number): void {
    const reach = Math.min(GLOW_MAX_REACH, r * (GLOW_REACH_BASE + GLOW_REACH_PER * (spans - 2)));
    ctx.fillStyle = GLOW_COLOR;
    for (let layer = GLOW_LAYERS; layer >= 1; layer--) {
      ctx.globalAlpha = GLOW_LAYER_ALPHA;
      ctx.beginPath();
      ctx.arc(x, y, reach * (layer / GLOW_LAYERS), 0, TAU);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  /** The disputed-identity beacons: a faint outer halo then the crisp ring, each ONE batched stroke. */
  private drawContestedRings(ctx: DrawContext, rings: { x: number; y: number; r: number }[]): void {
    if (rings.length === 0) return;
    ctx.strokeStyle = CONTESTED_RING_COLOR;
    /* The faint outer halo — the part that carries across the field and draws the eye to the question. */
    ctx.lineWidth = 1;
    ctx.globalAlpha = CONTESTED_HALO_ALPHA;
    ctx.beginPath();
    for (const ring of rings) {
      /* moveTo before each arc so the rings do not connect into one scribbled path. */
      ctx.moveTo(ring.x + ring.r + CONTESTED_HALO_PAD, ring.y);
      ctx.arc(ring.x, ring.y, ring.r + CONTESTED_HALO_PAD, 0, TAU);
    }
    ctx.stroke();
    this.stats.strokeBatches++;
    /* The ring itself — bright and crisp, so up close it reads as a deliberate mark, not a smudge. */
    ctx.lineWidth = CONTESTED_RING_WIDTH;
    ctx.globalAlpha = 1;
    ctx.beginPath();
    for (const ring of rings) {
      ctx.moveTo(ring.x + ring.r, ring.y);
      ctx.arc(ring.x, ring.y, ring.r, 0, TAU);
    }
    ctx.stroke();
    this.stats.strokeBatches++;
  }

  private drawShape(ctx: DrawContext, shape: RenderNode['shape'], x: number, y: number, r: number): void {
    ctx.beginPath();
    if (shape === 'square') {
      ctx.rect(x - r, y - r, r * 2, r * 2);
    } else if (shape === 'diamond') {
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y);
      ctx.lineTo(x, y + r);
      ctx.lineTo(x - r, y);
      ctx.closePath();
    } else {
      ctx.arc(x, y, r, 0, TAU);
    }
    ctx.fill();
  }

  /**
   * Gate, rank and place the names for a frame — the shared label decision behind BOTH the Canvas draw
   * and the GPU overlay, so the two renderers name the same figures in the same places.
   *
   * Gate first, then rank. A name earns ink only if its node is prominent enough for this zoom (its
   * intrinsic weight × zoom clears a few pixels), or the reader has zoomed in past the point where we
   * show everything that fits, or it is the selected node. Disputed figures are always eligible (like
   * the selected one) and ranked FIRST, so a reader sees WHO is disputed — the name, not just the ring
   * — even zoomed all the way out. Then most-important first, so where two names compete for the same
   * space the busier figure keeps its label. `onScreen` stays the FULL set for the circle-collision
   * test — a hidden name still must not be covered by a disc. `measure` is the caller's own text metric
   * (its 2D context), so the layout matches the surface the text is drawn on.
   */
  private selectLabels(onScreen: OnScreenNode[], measure: (text: string) => number): PlacedLabel[] {
    const z = this.cam.z;
    const showAll = z > LABEL_ZOOM_ALL;
    /*
     * NO FLOOR HERE, AND ONE WAS WRITTEN TWICE BEFORE THE NUMBERS SAID SO.
     *
     * The reasoning was: this gate is `weight × zoom` in screen pixels, a phone fits the field at a
     * third of a desktop's zoom, so on a phone nothing clears it and the map names nobody. Every
     * step of that is true except the conclusion. Measured with `stats.labelsDrawn` — added for this
     * question — a returning reader at 390px gets **9 names**, at 640px 14, at 1280px 17. Adding a
     * floor and removing it again produced byte-identical counts at all five widths.
     *
     * What looked like an unnamed map was two other things: a spec handing `WALKED_TRAIL` straight
     * to `addInitScript`, which injects a JSON string as script source and does nothing, so the
     * screenshots were of the ARRIVAL, where labels are suppressed on purpose; and a reader who
     * arrives with a figure open, where the panel is a sheet over most of a phone and only one name
     * has anywhere to go. See `arrivals-on-a-phone.spec.ts` for the floor that now holds this.
     */
    const candidates = onScreen
      .filter(o => o.pinned || o.contested || showAll || o.weight * z > LABEL_MIN_SCREEN_R)
      .sort((a, b) => (Number(b.contested) - Number(a.contested)) || (b.weight - a.weight));
    return placeLabels(candidates, onScreen, measure, { rightEdge: this.view.width, blocked: this.chrome });
  }

  /**
   * Name each wedge, and say how many figures its text alone holds.
   *
   * Returns the boxes it took, so the figure labels can be placed around them rather than through
   * them. Two labels are tried per wedge — the name and the count sit as one block — and a wedge
   * that cannot place goes unnamed: a caption over a neighbour's sky is worse than none.
   */
  private drawSectorLabels(ctx: DrawContext, W: number, H: number): { x0: number; y0: number; x1: number; y1: number }[] {
    const claimed: { x0: number; y0: number; x1: number; y1: number }[] = [];
    if (this.sectors.length === 0) return claimed;
    /* Gone once the reader is inside a sector and reading figures rather than orienting. */
    const fade = Math.min(1, Math.max(0, (SECTOR_ZOOM_GONE - this.cam.z) / SECTOR_ZOOM_SPAN));
    if (fade <= 0.02) return claimed;

    for (const sector of this.sectors) {
      if (sector.width < SECTOR_MIN_WIDTH) continue;
      this.stats.labelMeasures++;
      const half = ctx.measureText(sector.label).width / 2 + SECTOR_LABEL_PAD;

      const spot = this.placeSector(sector, half, W, H, claimed);
      if (!spot) continue;

      claimed.push({ x0: spot.x - half, y0: spot.y - 14, x1: spot.x + half, y1: spot.y + 23 });
      ctx.globalAlpha = fade;
      ctx.fillStyle = sector.colour;
      /* Centred on the spot: measureText gave the width, so half of it is the left edge. */
      ctx.fillText(sector.label, spot.x - (half - SECTOR_LABEL_PAD), spot.y);
      ctx.fillStyle = SECTOR_LABEL_COLOR_DIM;
      const beneath = `${sector.only} named here only`;
      this.stats.labelMeasures++;
      ctx.fillText(beneath, spot.x - ctx.measureText(beneath).width / 2, spot.y + 15);
    }
    ctx.globalAlpha = 1;
    return claimed;
  }

  /**
   * Find somewhere on this wedge the label fits: pull it in, then swing it round.
   *
   * Radius alone is not enough. The design records the case: a wedge pointing straight down runs
   * into the chrome at the foot of the screen, and every radius along that line is blocked — the
   * label simply never places. Both ladders are tried nearest-first, so a label moves as little as
   * it can, and the angle only swings within the wedge's own half-width so it never leans into the
   * sky next door.
   */
  private placeSector(
    sector: Sector,
    half: number,
    W: number,
    H: number,
    claimed: readonly { x0: number; y0: number; x1: number; y1: number }[],
  ): { x: number; y: number } | null {
    for (const pull of SECTOR_RADIUS_TRIES) {
      for (const swing of SECTOR_ANGLE_TRIES) {
        /* Never past the wedge's own edge, however far the ladder would swing. */
        if (Math.abs(swing) > sector.width / 2) continue;
        const a = sector.a + swing;
        const p = toScreen(
          { x: Math.cos(a) * sector.r * pull, y: Math.sin(a) * sector.r * pull },
          this.cam,
          this.view,
        );
        if (p.x - half < SECTOR_EDGE_INSET || p.x + half > W - SECTOR_EDGE_INSET) continue;
        if (p.y < 26 || p.y > H - 26) continue;
        const box = { x0: p.x - half, y0: p.y - 14, x1: p.x + half, y1: p.y + 23 };
        if (claimed.some(c => box.x0 < c.x1 && box.x1 > c.x0 && box.y0 < c.y1 && box.y1 > c.y0)) continue;
        /* And clear of the chrome — this is the case the design's angle ladder exists for: a wedge
           pointing into the bar at the foot of the screen has every radius along its line blocked. */
        if (this.chrome.some(c => box.x0 < c.x1 && box.x1 > c.x0 && box.y0 < c.y1 && box.y1 > c.y0)) continue;
        return { x: p.x, y: p.y };
      }
    }
    return null;
  }

  private drawLabels(ctx: DrawContext, onScreen: OnScreenNode[], claimed: readonly { x0: number; y0: number; x1: number; y1: number }[] = []): void {
    const placed = this.selectLabels(onScreen, text => { this.stats.labelMeasures++; return ctx.measureText(text).width; })
      /* A figure's name never covers a wedge's — the sector labels claimed their boxes first. */
      .filter(l => !claimed.some(c => l.box.x0 < c.x1 && l.box.x1 > c.x0 && l.box.y0 < c.y1 && l.box.y1 > c.y0));
    ctx.fillStyle = LABEL_COLOR;
    ctx.globalAlpha = 1;
    this.stats.labelsDrawn = placed.length;
    /* Positioned ENTIRELY from the reserved box, so a name pushed below its node draws below it and a
       name above draws above — the placer decides where, the draw just follows. box.x0 + 6 undoes the
       pad to the text's left edge (centred on the node); box.y0 + 13 is the baseline inside the box. */
    for (const label of placed) {
      ctx.fillText(label.text, label.box.x0 + 6, label.box.y0 + 13);
    }
  }
}
